import type { ReactNode } from 'react';
import { Badge } from 'v5/design-system/Badge';
import { cn } from 'v5/platform/dom/cn';
import { CrossfadeLayers } from 'v5/primitives/CrossfadeLayers';
import { FadeInImage } from 'v5/primitives/FadeInImage';
import { EntityAvatar } from './EntityOption';

/** An entity as its card header shows it. */
export type EntityCardHeaderData = {
  name: string;
  pictureUrl: string | null;
  /** What it is ("Organisation", "Agent"); `null` for nothing. */
  kind: string | null;
  /** A short note beside the kind (pronouns); `null` for none. */
  note: string | null;
};

/**
 * The top of an entity's card: its picture, name and kind over a cover made
 * from the picture. Fills its container's width; the host rounds and clips it.
 */
export function EntityCardHeader({
  entity,
  name,
  actions,
  flat
}: {
  entity: EntityCardHeaderData;
  /** Stands in for the name text, when the name itself is an action. */
  name?: ReactNode;
  /** In the top right corner; the name leaves room for it. */
  actions?: ReactNode;
  /** Draws no hairline at the bottom: the host separates the header itself. */
  flat?: boolean;
}) {
  return (
    <div className="relative isolate min-w-0 text-fg-default">
      {/* Switching entities: the old card stays until the new picture has
          loaded, then the new one fades in over it. */}
      <CrossfadeLayers
        layerKey={JSON.stringify([
          entity.pictureUrl,
          entity.name,
          entity.kind,
          entity.note
        ])}
      >
        <div className="relative isolate flex min-w-0 items-center gap-3 p-4">
          <EntityCardCover entity={entity} />
          <EntityAvatar entity={entity} size="lg" raised />
          {/* Multiplied, the badges take the cover's colours instead of grey patches. */}
          <div className="flex min-w-0 flex-col gap-1 mix-blend-multiply dark:mix-blend-normal">
            <span
              className={cn('flex min-w-0', actions !== undefined && 'pr-6')}
            >
              {name ?? (
                <span className="truncate text-lg font-semibold leading-tight">
                  {entity.name}
                </span>
              )}
            </span>
            {(entity.kind !== null || entity.note !== null) && (
              <span className="flex min-w-0 items-center gap-2">
                {entity.kind !== null && (
                  <Badge variant="tint" size="sm">
                    {entity.kind}
                  </Badge>
                )}
                {entity.note !== null && (
                  <span className="truncate text-sm text-fg-default/70">
                    {entity.note}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </CrossfadeLayers>
      {!flat && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-px bg-fg-default/4 mix-blend-multiply dark:mix-blend-normal"
        />
      )}
      {actions !== undefined && (
        <div className="absolute right-2 top-2 flex">{actions}</div>
      )}
    </div>
  );
}

/**
 * The header's background: the entity's picture enlarged and blurred (as the
 * media viewer's backdrop) under a veil of the card's colour, so the text
 * keeps its contrast whatever the picture; without a picture, plain and
 * neutral, as the entity header.
 */
function EntityCardCover({
  entity
}: {
  entity: Pick<EntityCardHeaderData, 'pictureUrl'>;
}) {
  if (entity.pictureUrl === null) {
    return <div aria-hidden className="absolute inset-0 -z-10 bg-bg-subtle" />;
  }
  return (
    <div
      aria-hidden
      // Neutral until the picture has loaded, then it fades in.
      className="absolute inset-0 -z-10 overflow-hidden bg-bg-subtle"
    >
      <FadeInImage
        src={entity.pictureUrl}
        alt=""
        className="absolute inset-0 size-full scale-[1.6] object-cover blur-2xl saturate-150"
      />
      <span className="absolute inset-0 bg-bg-default/70" />
    </div>
  );
}
